import React, { useState, useEffect } from "react";
import "./ServiceDetails.css";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import strings from "../../../../utils/strings";
import PatientDetails from "../patientDetails/PatientDetails";
import ServiceConfiguration from "../serviceConfiguration/ServiceConfiguration";
import NursePreferences from "../nursePref/NursePreferences";
import QuotationDetails from "../quotation/QuotationDetails";
import { getNurseRequestDetails, verifyNurseRequest, assignNurseRequest, saveDraftRequest } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { buildFileUrl, getFileNameFromUrl, getFileType } from "../../../../utils/fileHelpers";

const ServiceDetails = ({ setSelectedView }) => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const dispatch = useDispatch();
  const { requestDetails, newRequests } = useSelector((state) => state.manageRequest);

  // newRequests already has patientCondition from the list API — use as immediate fallback
  const requestFromList = newRequests?.find((r) => r._id === id);

  const breadcrumbs = ["nursingCare", "manageRequests", "serviceDetails"];
  const paths = [strings.navigation.manageNurseReq, strings.navigation.manageNurseReq, strings.navigation.viewNurseReqDetails];
  const steps = [t("patientDetails"), t("serviceDetails"), t("nursePref"), t("quotation")];
  const [enabledSteps, setEnabledSteps] = useState([1]);
  const [currentStep, setCurrentStep] = useState(1);
  const [page, setPage] = useState(1);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id) dispatch(getNurseRequestDetails({ id }));
  }, [id]);

  useEffect(() => {
    setCurrentStep(page);
  }, [page]);

  const formatAddress = (addr) => {
    if (!addr) return "";
    if (typeof addr === "string") return addr;
    return [addr.street, addr.ward, addr.city, addr.province, addr.postalCode].filter(Boolean).join(", ");
  };

  const sanitizeAddress = (addr) => {
    if (!addr || typeof addr === "string") return null;

    const address = { ...addr };

    const stringFields = [
      "personalCountryCode",
      "personalPhoneNumber",
      "street",
      "ward",
      "district",
      "postalCode",
      "city",
      "province"
    ];

    stringFields.forEach((field) => {
      if (address[field] !== undefined && address[field] !== null) {
        address[field] = String(address[field]);
      } else if (!address[field]) {
        address[field] = "";
      }
    });

    if (address.officeCountryCode || address.officePhoneNumber) {
      if (address.officeCountryCode) address.officeCountryCode = String(address.officeCountryCode);
      if (address.officePhoneNumber) address.officePhoneNumber = String(address.officePhoneNumber);
    }

    // Backend complains if optional string fields are passed as `null`.
    Object.keys(address).forEach((key) => {
      if (address[key] === null) {
        delete address[key];
      }
    });

    return address;
  };

  // Some requests carry patient info under `patient`, others (booked by a relative) under `relativeInfo`
  const person = requestDetails?.patient || requestDetails?.relativeInfo || {};

  const patientDetailsData = {
    name: person?.fullName || `${person?.firstName || ""}${person?.lastName ? " " + person.lastName : ""}`.trim() || "",
    gender: person?.gender || "",
    hnNumber: person?.hnNumber || "",
    insuranceType: person?.insuranceCategory || "",
    phone: person?.countryCode ? `+${person.countryCode} ${person.phoneNumber}` : person?.phoneNumber || "",
    email: person?.email || "",
    address: formatAddress(requestDetails?.address),
    addressFields: formData?.addressFields ?? requestDetails?.address,
    dateOfBirth: person?.dateOfBirth || "",
    attachments: (requestDetails?.attachments || []).filter(Boolean).map((a) => {
      if (typeof a === "string") {
        const name = getFileNameFromUrl(a) || "file";
        return { type: getFileType({ name }), url: buildFileUrl(a), name };
      }
      const rawUrl = a.url || a.path || a.fileUrl || a.fileURL || a.filePath || a.location || a.signedUrl || a.publicUrl || a.attachmentUrl || a.documentUrl || "";
      const url = buildFileUrl(rawUrl);
      const name = a.name || a.fileName || a.originalName || getFileNameFromUrl(rawUrl) || "file";
      return { type: getFileType({ name, mimeType: a.mimeType || a.mimetype || a.contentType || a.type }), url, name };
    }),
    description: formData?.description ?? requestDetails?.description ?? "",
    patientCondition: formData?.patientCondition ?? requestDetails?.patientCondition ?? requestFromList?.patientCondition ?? "",
    distanceInKm: formData?.distanceInKm ?? requestDetails?.distanceInKm ?? requestFromList?.distanceInKm ?? requestDetails?.zone ?? requestDetails?.address?.zone ?? "",
    profilePhoto: person?.profilePhoto || "",
    status: formData?.reviewedStatus || requestDetails?.status || requestFromList?.status || "OPEN",
    rejectReason: requestDetails?.rejectReason || requestFromList?.rejectReason || "",
    rejectedAt: requestDetails?.rejectedAt || requestFromList?.rejectedAt || "",
  };

  // Step 1 — POST assign API (Review API)
  const handleStep1Review = ({ patientCondition, addressFields, addressText, description, zone }) => {
    return new Promise((resolve, reject) => {
      const distanceInKm = parseFloat(zone) || 0;
      const address = sanitizeAddress(addressFields);
      setFormData((prev) => ({ ...prev, patientCondition, addressFields: address || addressFields, address, addressText, description, zone, distanceInKm }));

      const body = {
        distanceInKm,
        ...(patientCondition ? { patientCondition } : {}),
        ...(address ? { address } : {}),
        ...(description ? { description } : {}),
      };

      dispatch(assignNurseRequest({ id, body })).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          // Mark as reviewed in local formData so Review button stays disabled on back-navigation
          setFormData((prev) => ({ ...prev, reviewedStatus: "ASSIGNED" }));
          resolve(result);
        } else {
          import("../../../../widgets/Toast/toastPopup").then(({ default: ToastPopup }) => ToastPopup.error(result.error?.message || "Review failed"));
          reject(result.error);
        }
      });
    });
  };

  const handleStep1Next = ({ patientCondition, addressFields, addressText, description, zone }) => {
    const distanceInKm = parseFloat(zone) || 0;
    const address = sanitizeAddress(addressFields);
    const updated = { ...formData, patientCondition, addressFields: address || addressFields, address, addressText, description, zone, distanceInKm };
    setFormData(updated);

    // Build services from requestDetails — extract _id at any depth
    const step1Services = (requestDetails?.services || [])
      .map((svc) => {
        const serviceId = svc.service?._id || svc.service?.id || (typeof svc.service === "string" ? svc.service : null);
        const subServiceId = svc.subService?._id || svc.subService?.id || (typeof svc.subService === "string" ? svc.subService : null);
        const serviceItemId = svc.serviceItem?._id || svc.serviceItem?.id || (typeof svc.serviceItem === "string" ? svc.serviceItem : null);
        return serviceId ? { service: serviceId, subService: subServiceId, serviceItem: serviceItemId } : null;
      })
      .filter(Boolean);

    const draftBody = step1Services.length
      ? { ...buildDraftBody(updated), services: step1Services }
      : buildDraftBody(updated);
    dispatch(saveDraftRequest({ id, body: draftBody }));

    setEnabledSteps((prev) => [...new Set([...prev, 2])]);
    setPage(2);
  };

  const buildDraftBody = (accumulated) => {
    // Use step-2 services if available, otherwise fall back to requestDetails services.
    // Always extract plain IDs — requestDetails services may have populated nested objects.
    const sourceServices = accumulated.services?.length ? accumulated.services : requestDetails?.services || [];
    const services = sourceServices
      .map((svc) => ({
        service: svc.service?._id || (typeof svc.service === "string" ? svc.service : null),
        subService: svc.subService?._id || (typeof svc.subService === "string" ? svc.subService : null),
        serviceItem: svc.serviceItem?._id || (typeof svc.serviceItem === "string" ? svc.serviceItem : null),
      }))
      .filter((s) => s.service);

    const address = accumulated.address ?? sanitizeAddress(requestDetails?.address);
    const body = {
      draft: true,
      patientCondition: accumulated.patientCondition ?? requestDetails?.patientCondition ?? "",
      distanceInKm: accumulated.distanceInKm ?? requestDetails?.distanceInKm ?? 1,
      languagePreferences: accumulated.languagePreferences?.length ? accumulated.languagePreferences : requestDetails?.languagePreferences?.length ? requestDetails.languagePreferences : ["English"],
      genderPreferences: accumulated.genderPreferences?.length ? accumulated.genderPreferences : requestDetails?.genderPreferences?.length ? requestDetails.genderPreferences : ["Male"],
      nationality: requestDetails?.nationality || "Vietnamese",
      agePreference: { min: requestDetails?.agePreference?.min || 18, max: Math.min(requestDetails?.agePreference?.max || 65, 99) },
      price: String(accumulated.price ?? ""),
      quotationURL: accumulated.quotationURL ?? requestDetails?.quotationURL ?? null,
    };
    if (services.length) body.services = services;
    if (address) body.address = address;
    if (accumulated.description) body.description = accumulated.description;
    return body;
  };

  const handleStep2Save = ({ services, visits, startDate, endDate, frequencyMap }) => {
    setFormData((prev) => {
      const updated = { ...prev, services, visits, startDate, endDate, frequencyMap };
      dispatch(saveDraftRequest({ id, body: buildDraftBody(updated) }));
      return updated;
    });
  };

  const handleStep3Save = ({ languagePreferences, genderPreferences }) => {
    setFormData((prev) => {
      const updated = { ...prev, languagePreferences, genderPreferences };
      dispatch(saveDraftRequest({ id, body: buildDraftBody(updated) }));
      return updated;
    });
  };

  const handleFinalSubmit = ({ quotationURL, price, quotationItems }) => {
    const uploadedQuotationURL = quotationURL?.trim() || "";
    const accumulated = { ...formData, quotationURL: uploadedQuotationURL, price, quotationItems };
    const firstVisit = accumulated.visits?.[0] || {};

    // Use edited services from Step 2 when present; fall back to GET API services.
    const sourceServices = accumulated.services?.length ? accumulated.services : requestDetails?.services || [];
    const services = sourceServices
      .map((svc, index) => {
        const service = svc.service?._id || svc.service;
        const subService = svc.subService?._id || svc.subService;
        const serviceItem = svc.serviceItem?._id || svc.serviceItem;
        const serviceBody = { service, subService, serviceItem };

        if (svc.startTime) serviceBody.startTime = svc.startTime;
        if (svc.endTime) serviceBody.endTime = svc.endTime;
        if (svc.startDate) serviceBody.startDate = svc.startDate;
        if (svc.endDate) serviceBody.endDate = svc.endDate;
        if (svc.singleDate || svc.date) serviceBody.startDate = svc.singleDate || svc.date;
        if (svc.frequencyMap || svc.customFrequencyMap) serviceBody.frequencyMap = svc.frequencyMap || svc.customFrequencyMap;

        // Itemized quotation pricing (index-aligned with quotationItems — see QuotationDetails.jsx)
        const pricing = accumulated.quotationItems?.[index];
        if (pricing) {
          serviceBody.per_time = pricing.per_time ?? 1;
          serviceBody.serviceFee = Number(pricing.serviceFee) || 0;
          serviceBody.transportFee = Number(pricing.transportFee) || 0;
          serviceBody.additionalFee = Number(pricing.additionalFee) || 0;
        }

        return serviceBody;
      })
      .filter((s) => s.service);

    const rawAge = requestDetails?.agePreference;
    const agePreference = {
      min: rawAge?.min > 0 ? rawAge.min : 18,
      max: rawAge?.max > 0 && rawAge.max < 100 ? rawAge.max : 65,
    };

    const address = accumulated.address ?? sanitizeAddress(requestDetails?.address);
    const description = accumulated.description ?? requestDetails?.description;
    const startDate = accumulated.startDate || firstVisit.startDate || firstVisit.singleDate || firstVisit.date || requestDetails?.startDate;
    const endDate = accumulated.endDate || firstVisit.endDate || requestDetails?.endDate;
    const frequencyMap = accumulated.frequencyMap || firstVisit.frequencyMap || firstVisit.customFrequencyMap || requestDetails?.frequencyMap;

    const body = {
      draft: false,
      services,
      patientCondition: accumulated.patientCondition ?? requestDetails?.patientCondition ?? "",
      distanceInKm: accumulated.distanceInKm ?? requestDetails?.distanceInKm ?? 1,
      languagePreferences: accumulated.languagePreferences?.length ? accumulated.languagePreferences : requestDetails?.languagePreferences?.length ? requestDetails.languagePreferences : ["English"],
      genderPreferences: accumulated.genderPreferences?.length ? accumulated.genderPreferences : requestDetails?.genderPreferences?.length ? requestDetails.genderPreferences : ["Male"],
      nationality: requestDetails?.nationality || "Vietnamese",
      agePreference,
      price: String(accumulated.price ?? ""),
      quotationURL: uploadedQuotationURL || null,
    };

    if (address) body.address = address;
    if (description) body.description = description;
    if (startDate) body.startDate = startDate;
    if (endDate) body.endDate = endDate;
    if (frequencyMap) body.frequencyMap = frequencyMap;

    dispatch(verifyNurseRequest({ id, body })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        nav(strings.navigation.manageNurseReq);
      } else {
        import("../../../../widgets/Toast/toastPopup").then(({ default: ToastPopup }) => ToastPopup.error(result.error?.message || "Failed to send quote"));
      }
    });
  };

  const quotationPdfData = {
    patient: {
      hnNumber: patientDetailsData.hnNumber,
      name: patientDetailsData.name,
      gender: patientDetailsData.gender,
      dob: patientDetailsData.dateOfBirth,
      phone: patientDetailsData.phone,
      email: patientDetailsData.email,
      address: patientDetailsData.address,
    },
    request: {
      requestId: requestDetails?.nursingRequestID || id,
      description: patientDetailsData.description,
      patientCondition: patientDetailsData.patientCondition,
      distanceInKm: patientDetailsData.distanceInKm,
      nationality: requestDetails?.nationality || "",
      preferredGender: (formData.genderPreferences?.length ? formData.genderPreferences : requestDetails?.genderPreferences || []).join(", "),
      preferredLanguage: (formData.languagePreferences?.length ? formData.languagePreferences : requestDetails?.languagePreferences || []).join(", "),
      preferredAge: requestDetails?.agePreference ? `${requestDetails.agePreference.min || ""}-${requestDetails.agePreference.max || ""}` : "",
    },
  };

  const handleStepChange = (stepIndex) => {
    if (enabledSteps.includes(stepIndex)) {
      setCurrentStep(stepIndex);
      setPage(stepIndex);
    }
  };

  return (
    <div className="service-details-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="department-heading">{t("generateQuotation")}</div>
      <Widgets.ProgressBar steps={steps} currentStep={currentStep} enabledSteps={enabledSteps} onStepChange={handleStepChange} />

      {page === 1 && <PatientDetails setPage={setPage} setEnabledSteps={setEnabledSteps} data={patientDetailsData} onReview={handleStep1Review} onNext={handleStep1Next} rejectReason={patientDetailsData.rejectReason} rejectedAt={patientDetailsData.rejectedAt} />}

      {page === 2 && <ServiceConfiguration setPage={setPage} setEnabledSteps={setEnabledSteps} initialServices={
        // Re-hydrate from saved visits when navigating back, otherwise use API data
        formData.visits?.length
          ? formData.visits.map((v) => ({
              service: { _id: v.serviceId, title: v.serviceTitle || { en: v.serviceName, vi: v.serviceName } },
              subService: { _id: v.subServiceId },
              serviceItem: { _id: v.serviceItemId, title: v.serviceItemTitle || { en: v.serviceItemName, vi: v.serviceItemName }, ...(v.serviceItemData || {}) },
              startTime: v.startTime,
              endTime: v.endTime,
              startDate: v.startDate,
              endDate: v.endDate,
              singleDate: v.singleDate,
              isMultipleDay: v.isMultipleDay,
            }))
          : requestDetails?.services || []
      } onSave={handleStep2Save} />}

      {page === 3 && <NursePreferences setPage={setPage} setEnabledSteps={setEnabledSteps} onSave={handleStep3Save} initialLanguage={formData.languagePreferences?.[0] ?? requestDetails?.languagePreferences?.[0] ?? "Vietnamese"} initialGender={formData.genderPreferences?.[0] ?? requestDetails?.genderPreferences?.[0] ?? "Female"} />}

      {page === 4 && (
        <QuotationDetails
          setPage={setPage}
          visitsData={formData.visits || []}
          distanceInKm={formData.distanceInKm ?? patientDetailsData.distanceInKm}
          existingQuotationURL={formData.quotationURL ?? requestDetails?.quotationURL ?? ""}
          persistedServices={requestDetails?.services || []}
          quotationPdfData={quotationPdfData}
          onFinalSubmit={handleFinalSubmit}
        />
      )}
    </div>
  );
};

export default ServiceDetails;
